import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import get from 'lodash/get';

import { Select } from '../Select/Select';
import { useDebounce } from '../../../hooks/useDebounce';

import type { CnpTag, CnpStory } from '../../../types';
import type { Option, SelectProps } from '../Select/Select';

export type AsyncSelectProps<T> = {
  fetchMany: (query: string) => Promise<T[]>;
  fetchOne: (id: string) => Promise<T>;
  mapItemToOption: (item: T) => Option;
} & Omit<
  SelectProps,
  'onInputChange' | 'options' | 'isLoading' | 'placeholder'
>;

function AsyncSelect<T extends CnpTag | CnpStory>({
  fetchMany,
  fetchOne,
  mapItemToOption,
  isMulti = false,
  ...props
}: AsyncSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isInitiallySelectedFetched, setIsInitiallySelectedFetched] =
    useState(false);
  const { values } = useFormikContext();
  const { name } = props;

  const debouncedQuery = useDebounce(query, 300);

  const handleOptionsFetch = useCallback(
    (items: T[]) => {
      setOptions(items.map(mapItemToOption));
    },
    [mapItemToOption]
  );

  const fetchOptions = useCallback(
    async function (query: string) {
      try {
        const items = await fetchMany(query);
        handleOptionsFetch(items);
      } catch (error) {
        console.error('Failed fetching async select items...', error);
      }
    },
    [fetchMany, handleOptionsFetch]
  );

  const fetchInitiallySelected = useCallback(
    async function (entityId: string | string[]) {
      setIsFetching(true);

      try {
        if (isMulti) {
          const items = await Promise.all(
            (entityId as string[]).map((id) => fetchOne(id))
          );
          handleOptionsFetch(items);
        } else {
          const item = await fetchOne(entityId as string);
          handleOptionsFetch([item]);
        }
      } catch (error) {
        console.error(
          'Failed fetching async select initially selected items...',
          error
        );
      }

      setIsInitiallySelectedFetched(true);
      setIsFetching(false);
    },
    [fetchOne, handleOptionsFetch, isMulti]
  );

  useEffect(() => {
    if (debouncedQuery) {
      fetchOptions(debouncedQuery);
    } else if (!get(values, name as string)) {
      setOptions([]);
    }
  }, [debouncedQuery, fetchOptions, name, values]);

  useEffect(() => {
    const value = get(values, name as string);

    if (value && !isInitiallySelectedFetched) {
      fetchInitiallySelected(value);
    }
  }, [fetchInitiallySelected, isInitiallySelectedFetched, name, values]);

  return (
    <Select
      {...props}
      onInputChange={setQuery}
      options={options}
      isLoading={isFetching}
      isMulti={isMulti}
      placeholder="Start typing to get suggestions..."
    />
  );
}

export { AsyncSelect };
